import { Injectable } from '@angular/core';
import { GridService } from '../shared/grid/grid.service';
import {
        LinkedSensorAssignment,
        Parameter,
        ParameterApiResponse,
        ParameterListApiResponse,
        ParameterListResponse,
        ParameterLookup,
        ParameterThreshold
} from '../models/parameter.model';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OrganizationLevel } from '../models/organizationLevel.model';
import { MeasurementModeEnum } from '../enums/measurementMode.enum';

@Injectable({
    providedIn: 'root'
})
export class ParameterService extends GridService<Parameter> {
    allParametersData: Parameter[] = [];

    constructor(http: HttpClient) {
        super('Parameter', http);
    }

    override getAll(filterBy: string = ''): Observable<ParameterLookup[]> {
        return this.http.get<ParameterLookup[]>(this.getApiUrl(`${this.controllerName}/GetAll?filterBy=${filterBy}`));
    }

    getAllParameters(filterBy?: string, excludeNotificationOnly: boolean = false): Observable<Parameter[]> {
        let url = this.getApiUrl('Parameter/GetAll');

        const params = new URLSearchParams();
        if (filterBy) {
            params.append('filterBy', filterBy);
        }
        params.append('excludeNotificationOnly', excludeNotificationOnly.toString());

        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        return this.http.get<ParameterApiResponse[]>(url).pipe(
            map((response: ParameterApiResponse[]) => {
                return response.map(item => this.mapApiResponseToParameter(item)) as Parameter[];
            })
        );
    }

    getParameterList(pageSize: number = 10, pageNumber: number = 1, sortBy?: string, filterBy?: string): Observable<ParameterListResponse> {
        let url = this.getApiUrl('Parameter/GetList');

        const params = new URLSearchParams();
        params.append('pageSize', pageSize.toString());
        params.append('pageNumber', pageNumber.toString());

        if (sortBy) {
            params.append('sortBy', sortBy);
        }
        if (filterBy) {
            params.append('filterBy', filterBy);
        }

        if (params.toString()) {
            url += `?${params.toString()}`;
        }

        return this.http.get<ParameterListApiResponse>(url).pipe(
            map((response: ParameterListApiResponse) => {
                // Map the data array in the response
                if (response && response.data && Array.isArray(response.data)) {
                    return {
                        ...response,
                        data: response.data.map((item: ParameterApiResponse) => this.mapApiResponseToParameter(item))
                    };
                }
                return {
                    ...response,
                    data: []
                };
            })
        );
    }

    private mapApiResponseToParameter(item: ParameterApiResponse): Parameter {
        const parameter = new Parameter();

        // Direct field mappings from API response
        parameter.id = item.id;
        parameter.analyteName = item.name || item.analyteName || '';
        parameter.parameterType = typeof item.parameterType === 'string'
            ? parseInt(item.parameterType, 10)
            : item.parameterType;
        parameter.type = typeof item.type === 'string' ? parseInt(item.type, 10) : item.type;
        parameter.healthAdvisory = item.healthAdvisory;
        parameter.specificRecommendations = item.specificRecommendations;
        parameter.averagingType = item.averagingType;
        parameter.averagingTimeSeconds = item.averagingTimeSeconds;
        parameter.isNotificationOnly = item.isNotificationOnly ?? false;
        parameter.measurementMode = item.measurementMode;
        parameter.organizationLevelId = item.organizationLevelId;
        parameter.sampleTypeId = item.sampleTypeId;
        parameter.measurementMode = typeof item.measurementMode === 'string'
            ? parseInt(item.measurementMode, 10) as MeasurementModeEnum
            : (item.measurementMode as MeasurementModeEnum | undefined);

        if (item.thresholds && Array.isArray(item.thresholds)) {
            const thresholds = item.thresholds;
            parameter.thresholds = thresholds.map((thresholdItem, index: number) => {
                const t = thresholdItem as Partial<ParameterThreshold>;
                const isLastThreshold = index === thresholds.length - 1;

                return {
                    id: t.id,
                    parameterId: t.parameterId ?? parameter.id,
                    label: t.label ?? '',
                    minValue: t.minValue ?? 0,
                    maxValue: isLastThreshold ? null : (t.maxValue ?? null),
                    colorHex: t.colorHex ?? '#000000',
                    sortOrder: t.sortOrder ?? index
                };
            });
        }

        return parameter;
    }

    getParameterById(id: string): Observable<Parameter> {
        const url = this.getApiUrl(`Parameter/Get?id=${id}`);

        return this.http.get<ParameterApiResponse>(url).pipe(
            map((response: ParameterApiResponse) => this.mapApiResponseToParameter(response))
        );
    }

    getThresholds(parameterId: string): Observable<ParameterThreshold[]> {
        return this.http.get<ParameterThreshold[]>(
            this.getApiUrl(`${this.controllerName}/${parameterId}/GetThresholds`)
        );
    }

    bulkSaveThresholds(parameterId: string, thresholds: ParameterThreshold[]): Observable<ParameterThreshold[]> {
        return this.http.post<ParameterThreshold[]>(
            this.getApiUrl(`${this.controllerName}/${parameterId}/BulkSaveThresholds`),
            { thresholds }
        );
    }

    getOrganizationLevels(): Observable<OrganizationLevel[]> {
        return this.http.get<OrganizationLevel[]>(this.getApiUrl('OrganizationLevel/GetAll'));
    }

    hasActiveSensorAssignments(parameterId: string): Observable<boolean> {
        return this.http.get<boolean>(this.getApiUrl(`Parameter/HasActiveSensorAssignments?parameterId=${parameterId}`));
    }

    /**
     * Get linked sensor assignments for a parameter
     * Used by delete dialog to show which sensors are using this parameter
     *
     * @param parameterId - Parameter ID
     * @returns Observable array of linked sensor assignments
     */
    getLinkedSensorAssignments(parameterId: string): Observable<LinkedSensorAssignment[]> {
        return this.http.get<LinkedSensorAssignment[]>(this.getApiUrl(`Parameter/GetLinkedSensorAssignments?parameterId=${parameterId}`));
    }

    getAllExcludingNotificationOnly(filterBy: string = ''): Observable<ParameterLookup[]> {
        return this.http.get<ParameterLookup[]>(this.getApiUrl(`${this.controllerName}/GetAll`), {
            params: { filterBy, excludeNotificationOnly: 'true' }
        });
    }
}
